/* ============================================================================
   hydr8.js — vanilla port of the React prototype's behaviour.
   Every interaction below mirrors the JSX build 1:1 so the Shopify theme
   renders and animates identically. No framework, no build step.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var clamp = function (v, a, b) { return Math.min(b, Math.max(a, v)); };

  /* ---------- Reveal: fade + rise on scroll-into-view ---------------------- */
  function initReveal() {
    var els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;

    if (REDUCE || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.style.opacity = '1'; el.style.transform = 'none'; });
      return;
    }

    function inHorizontalScroller(el) {
      for (var n = el.parentElement; n && n !== document.body; n = n.parentElement) {
        var ox = getComputedStyle(n).overflowX;
        if ((ox === 'auto' || ox === 'scroll') && n.scrollWidth > n.clientWidth + 1) return true;
      }
      return false;
    }

    els.forEach(function (el) {
      // A card in a swipeable row would otherwise sit invisible and jump upward
      // the first time it is swiped into view. Reveal those up front.
      if (inHorizontalScroller(el)) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.setAttribute('data-reveal-skip', '');
        return;
      }
      var delay = el.getAttribute('data-reveal-delay') || 0;
      var y = el.getAttribute('data-reveal-y') || 26;
      var scale = el.hasAttribute('data-reveal-scale') ? ' scale(.94)' : '';
      el.style.opacity = '0';
      el.style.transform = 'translateY(' + y + 'px)' + scale;
      el.style.transition = 'opacity .7s var(--ease-out) ' + delay + 'ms, transform .7s var(--ease-out) ' + delay + 'ms';
      el.style.willChange = 'opacity, transform';
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.style.opacity = '1';
        e.target.style.transform = 'none';
        io.unobserve(e.target);
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });

    els.forEach(function (el) { if (!el.hasAttribute('data-reveal-skip')) io.observe(el); });
  }

  /* ---------- TwoTone: ghost → solid wipe on headings --------------------- */
  function initTwoTone() {
    var els = document.querySelectorAll('[data-twotone]');
    if (!els.length) return;

    els.forEach(function (el) {
      var onDark = el.hasAttribute('data-twotone-dark');
      var solid = onDark ? '#FFFFFF' : 'var(--ink)';
      var ghost = onDark ? 'rgba(255,255,255,.22)' : 'var(--mist)';

      if (REDUCE || !('IntersectionObserver' in window)) { el.style.color = solid; return; }

      el.style.backgroundImage = 'linear-gradient(90deg, ' + solid + ' 0%, ' + solid + ' 50%, ' + ghost + ' 50%, ' + ghost + ' 100%)';
      el.style.backgroundSize = '200% 100%';
      el.style.backgroundPositionX = '100%';
      el.style.webkitBackgroundClip = 'text';
      el.style.backgroundClip = 'text';
      el.style.webkitTextFillColor = 'transparent';
      el.style.color = 'transparent';
      el.style.transition = 'background-position-x 1s var(--ease-out)';

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.style.backgroundPositionX = '0%';
          io.unobserve(e.target);
        });
      }, { threshold: 0.5, rootMargin: '0px 0px -8% 0px' });
      io.observe(el);
    });
  }

  /* ---------- Hero carousel ---------------------------------------------- */
  function initHero() {
    var hero = document.querySelector('[data-hero]');
    if (!hero) return;

    var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-slide]'));
    if (!slides.length) return;

    var dots = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-dot]'));
    var active = 0;
    var paused = false;
    var timer = null;

    function paint() {
      slides.forEach(function (slide, i) {
        var on = i === active;
        slide.style.opacity = on ? '1' : '0';
        slide.style.pointerEvents = on ? 'auto' : 'none';

        var img = slide.querySelector('[data-hero-img]');
        if (img) img.style.transform = on ? 'scale(1)' : 'scale(1.025)';

        // Re-run the entrance animations exactly like the React remount did.
        var pill = slide.querySelector('[data-hero-pill]');
        var body = slide.querySelector('[data-hero-body]');
        var lines = slide.querySelectorAll('[data-hero-line]');

        if (REDUCE) {
          if (pill) pill.style.opacity = on ? '1' : '0';
          if (body) body.style.opacity = on ? '1' : '0';
          return;
        }

        if (pill) pill.style.animation = on ? 'fadeRise .7s var(--ease-out) both' : 'none';
        if (body) body.style.animation = on ? 'fadeRise .7s var(--ease-out) .45s both' : 'none';
        lines.forEach(function (line, li) {
          line.style.animation = on
            ? 'clipUp .8s var(--ease-out) ' + (0.12 + li * 0.12) + 's both'
            : 'none';
        });
      });

      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === active); });
    }

    function go(step) {
      active = (active + step + slides.length) % slides.length;
      paint();
    }

    function start() {
      stop();
      if (slides.length < 2) return;
      timer = setInterval(function () { if (!paused) go(1); }, 5000);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    hero.addEventListener('mouseenter', function () { paused = true; });
    hero.addEventListener('mouseleave', function () { paused = false; });

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { active = i; paint(); });
    });
    hero.querySelectorAll('[data-hero-prev]').forEach(function (b) {
      b.addEventListener('click', function () { go(-1); });
    });
    hero.querySelectorAll('[data-hero-next]').forEach(function (b) {
      b.addEventListener('click', function () { go(1); });
    });

    paint();
    start();
  }

  /* ---------- Flavour rail: pinned horizontal scroll (desktop only) -------
     Mirrors FlavourShowcase: the track translates as the tall wrapper scrolls,
     finishing its travel at 72% of the pin so the last card holds on screen. */
  function initFlavourRail() {
    var wrap = document.querySelector('[data-rail]');
    if (!wrap) return;
    var track = wrap.querySelector('[data-rail-track]');
    if (!track) return;

    var raf = null;

    function isDesktop() { return window.innerWidth >= 900; }

    function onScroll() {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        if (!isDesktop() || REDUCE) { track.style.transform = ''; return; }

        var rect = wrap.getBoundingClientRect();
        var dist = wrap.offsetHeight - window.innerHeight;
        if (dist <= 0) { track.style.transform = ''; return; }

        var prog = clamp(-rect.top / dist, 0, 1);
        var lastCard = track.lastElementChild;
        var viewportGutter = window.innerWidth >= 1320
          ? ((window.innerWidth - 1320) / 2) + 48
          : 48;
        var maxX = lastCard
          ? Math.max(0, lastCard.offsetLeft + lastCard.offsetWidth + viewportGutter - window.innerWidth)
          : Math.max(0, track.scrollWidth - window.innerWidth + 96);

        var railProg = Math.min(1, prog / 0.72);
        track.style.transform = 'translateX(' + (-railProg * maxX) + 'px)';
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
  }

  /* ---------- "What's inside": scroll-linked fact stepper ------------------ */
  function initInside() {
    var wrap = document.querySelector('[data-inside]');
    if (!wrap) return;

    var facts = Array.prototype.slice.call(wrap.querySelectorAll('[data-inside-fact]'));
    var packs = Array.prototype.slice.call(wrap.querySelectorAll('[data-inside-pack]'));
    var bars = Array.prototype.slice.call(wrap.querySelectorAll('[data-inside-bar]'));
    var visual = wrap.querySelector('[data-inside-visual]');
    var bigEl = wrap.querySelector('[data-inside-big]');
    var statEl = wrap.querySelector('[data-inside-stat]');
    var cardBig = wrap.querySelector('[data-inside-card-big]');
    var cardLabel = wrap.querySelector('[data-inside-card-label]');
    var cardDesc = wrap.querySelector('[data-inside-card-desc]');
    if (!facts.length) return;

    var active = -1;
    var raf = null;

    function setActive(i) {
      if (i === active) return;
      active = i;
      var f = facts[i];
      if (!f) return;

      facts.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
      packs.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
      bars.forEach(function (el, n) { el.classList.toggle('is-lit', n <= i); });

      var accent = f.getAttribute('data-accent') || 'var(--coral)';
      if (visual) visual.style.setProperty('--active-accent', accent);
      if (bigEl) bigEl.textContent = f.getAttribute('data-big') || '';
      if (statEl) statEl.textContent = f.getAttribute('data-stat') || '';
      if (cardBig) cardBig.textContent = f.getAttribute('data-big') || '';
      if (cardLabel) cardLabel.textContent = f.getAttribute('data-label') || '';
      if (cardDesc) cardDesc.textContent = f.getAttribute('data-desc') || '';
    }

    function onScroll() {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        var rect = wrap.getBoundingClientRect();
        var dist = Math.max(1, wrap.offsetHeight - window.innerHeight);
        var progress = clamp(-rect.top / dist, 0, 1);
        setActive(Math.min(facts.length - 1, Math.floor(progress * facts.length)));
      });
    }

    // Clicking a fact scrolls to its slice of the pinned runway.
    facts.forEach(function (f, i) {
      f.addEventListener('click', function () {
        var dist = wrap.offsetHeight - window.innerHeight;
        var top = wrap.offsetTop + (dist * (i / Math.max(1, facts.length - 1)));
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    setActive(0);
    onScroll();
  }

  /* ---------- Accordions: FAQ + PDP panels -------------------------------- */
  function initAccordions() {
    // FAQ — one open at a time
    document.querySelectorAll('[data-faq]').forEach(function (list) {
      var rows = Array.prototype.slice.call(list.querySelectorAll('.faq-row'));
      rows.forEach(function (row, i) {
        var btn = row.querySelector('.faq-q');
        var body = row.querySelector('.faq-a');
        if (!btn || !body) return;
        var open = i === 0;
        row.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        body.style.gridTemplateRows = open ? '1fr' : '0fr';

        btn.addEventListener('click', function () {
          var willOpen = !row.classList.contains('is-open');
          rows.forEach(function (r) {
            r.classList.remove('is-open');
            var b = r.querySelector('.faq-a');
            var q = r.querySelector('.faq-q');
            if (b) b.style.gridTemplateRows = '0fr';
            if (q) q.setAttribute('aria-expanded', 'false');
          });
          if (willOpen) {
            row.classList.add('is-open');
            body.style.gridTemplateRows = '1fr';
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });

    // PDP panels — independent toggles
    document.querySelectorAll('[data-panel]').forEach(function (panel) {
      var btn = panel.querySelector('[data-panel-btn]');
      var body = panel.querySelector('[data-panel-body]');
      var icon = panel.querySelector('[data-panel-icon]');
      if (!btn || !body) return;
      body.style.maxHeight = '0px';
      body.style.opacity = '0';
      btn.addEventListener('click', function () {
        var open = panel.classList.toggle('is-open');
        body.style.maxHeight = open ? body.scrollHeight + 'px' : '0px';
        body.style.opacity = open ? '1' : '0';
        if (icon) icon.style.transform = open ? 'rotate(45deg)' : 'none';
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ---------- Mobile nav --------------------------------------------------- */
  function initNav() {
    var nav = document.querySelector('[data-nav]');
    if (!nav) return;
    var burger = nav.querySelector('[data-nav-burger]');
    var panel = document.querySelector('[data-nav-panel]');
    var solidAtTop = nav.hasAttribute('data-nav-solid');

    function setSolid(solid) {
      nav.classList.toggle('is-solid', solid);
    }

    if (!solidAtTop) {
      var onScroll = function () { setSolid(window.scrollY > 56 || nav.classList.contains('is-open')); };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    } else {
      setSolid(true);
    }

    if (burger && panel) {
      burger.addEventListener('click', function () {
        var open = panel.classList.toggle('is-open');
        nav.classList.toggle('is-open', open);
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.body.style.overflow = open ? 'hidden' : '';
        if (open) setSolid(true);
        else if (!solidAtTop) setSolid(window.scrollY > 56);
      });
    }
  }

  /* ---------- Cart drawer (Shopify AJAX) ---------------------------------- */
  var Cart = {
    formatMoney: function (cents) {
      // shop.money_format may carry HTML entities; strip the placeholder and
      // fall back to a plain "Rs. 1,200" style if the format is unusual.
      var amount = (cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
      var fmt = window.hydr8 && window.hydr8.moneyFormat;
      if (fmt && fmt.indexOf('{{') !== -1) {
        return fmt.replace(/\{\{\s*\w+\s*\}\}/, amount);
      }
      return 'Rs. ' + amount;
    },

    open: function () {
      var d = document.querySelector('[data-cart-drawer]');
      var o = document.querySelector('[data-cart-overlay]');
      if (d) d.classList.add('is-open');
      if (o) o.classList.add('is-open');
    },
    close: function () {
      var d = document.querySelector('[data-cart-drawer]');
      var o = document.querySelector('[data-cart-overlay]');
      if (d) d.classList.remove('is-open');
      if (o) o.classList.remove('is-open');
    },

    fetch: function () {
      return fetch(window.hydr8.routes.cart_url + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { Cart.render(cart); return cart; });
    },

    add: function (payload) {
      return fetch(window.hydr8.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add to cart'); });
          return r.json();
        })
        .then(function () { return Cart.fetch(); })
        .then(function (cart) { Cart.open(); return cart; });
    },

    change: function (key, quantity) {
      return fetch(window.hydr8.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) { Cart.render(cart); return cart; });
    },

    render: function (cart) {
      var itemsEl = document.querySelector('[data-cart-items]');
      var footEl = document.querySelector('[data-cart-foot]');
      var totalEl = document.querySelector('[data-cart-total]');
      var countEls = document.querySelectorAll('[data-cart-count]');

      countEls.forEach(function (el) {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      var titleEl = document.querySelector('[data-cart-title]');
      if (titleEl) titleEl.textContent = cart.item_count > 0 ? 'Cart (' + cart.item_count + ')' : 'Cart';

      if (!itemsEl) return;

      if (!cart.items.length) {
        itemsEl.innerHTML = '<p style="color:var(--steel);text-align:center;margin-top:56px;font-size:16px">Your cart is empty.</p>';
        if (footEl) footEl.style.display = 'none';
        return;
      }

      if (footEl) footEl.style.display = 'block';
      if (totalEl) totalEl.textContent = Cart.formatMoney(cart.total_price);

      itemsEl.innerHTML = cart.items.map(function (item) {
        var img = item.image
          ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_140x$1$2')
          : '';
        // selling plan name (subscription) shown under the title, like the local build's pack line
        var sub = item.selling_plan_allocation
          ? item.selling_plan_allocation.selling_plan.name
          : (item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '');
        return '' +
          '<div style="display:flex;gap:14px;padding:16px 0;border-bottom:1px solid var(--mist)">' +
            '<img src="' + img + '" alt="" style="width:70px;height:70px;object-fit:cover;border-radius:12px;background:var(--paper);flex-shrink:0">' +
            '<div style="flex:1;min-width:0">' +
              '<div style="font-weight:700;font-size:16px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + item.product_title + '</div>' +
              '<div style="font-size:14px;color:var(--steel);margin-bottom:8px">' + sub + '</div>' +
              '<div style="display:flex;align-items:center;gap:10px">' +
                '<button class="qty-btn" data-cart-minus="' + item.key + '" aria-label="Decrease quantity" style="width:40px;height:40px;border-radius:50%;border:1.5px solid var(--mist);font-size:16px;font-weight:700;display:flex;align-items:center;justify-content:center">&minus;</button>' +
                '<span style="font-weight:700;font-size:15px;min-width:18px;text-align:center">' + item.quantity + '</span>' +
                '<button class="qty-btn" data-cart-plus="' + item.key + '" aria-label="Increase quantity" style="width:40px;height:40px;border-radius:50%;border:1.5px solid var(--mist);font-size:16px;font-weight:700;display:flex;align-items:center;justify-content:center">+</button>' +
              '</div>' +
            '</div>' +
            '<div style="display:flex;flex-direction:column;align-items:flex-end;justify-content:space-between;gap:10px">' +
              '<div style="font-weight:700;font-size:15px;white-space:nowrap">' + Cart.formatMoney(item.final_line_price) + '</div>' +
              '<button type="button" class="h8-cart-remove" data-cart-remove="' + item.key + '" title="Remove" aria-label="Remove ' + String(item.product_title).replace(/"/g, '&quot;') + ' from cart">' +
                '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
                  '<polyline points="3 6 5 6 21 6"></polyline>' +
                  '<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>' +
                  '<line x1="10" y1="11" x2="10" y2="17"></line>' +
                  '<line x1="14" y1="11" x2="14" y2="17"></line>' +
                '</svg>' +
              '</button>' +
            '</div>' +
          '</div>';
      }).join('');

      itemsEl.querySelectorAll('[data-cart-minus]').forEach(function (b) {
        b.addEventListener('click', function () {
          var key = b.getAttribute('data-cart-minus');
          var item = cart.items.filter(function (i) { return i.key === key; })[0];
          Cart.change(key, Math.max(0, item.quantity - 1));
        });
      });
      itemsEl.querySelectorAll('[data-cart-plus]').forEach(function (b) {
        b.addEventListener('click', function () {
          var key = b.getAttribute('data-cart-plus');
          var item = cart.items.filter(function (i) { return i.key === key; })[0];
          Cart.change(key, item.quantity + 1);
        });
      });
      itemsEl.querySelectorAll('[data-cart-remove]').forEach(function (b) {
        b.addEventListener('click', function () {
          Cart.change(b.getAttribute('data-cart-remove'), 0);
        });
      });
    }
  };

  function initCart() {
    document.querySelectorAll('[data-cart-open]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); Cart.open(); });
    });
    document.querySelectorAll('[data-cart-close]').forEach(function (b) {
      b.addEventListener('click', Cart.close);
    });
    var overlay = document.querySelector('[data-cart-overlay]');
    if (overlay) overlay.addEventListener('click', Cart.close);

    // Any product form on the page adds via AJAX and opens the drawer.
    document.querySelectorAll('[data-product-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[data-add-btn]');
        // An icon-only button (the rail's 44px circle) has an SVG and no label.
        // Setting text on it spills out of the circle, so swap the glyph instead.
        var isIcon = !!(btn && btn.querySelector('svg') && !btn.textContent.trim());
        var original = btn ? (isIcon ? btn.innerHTML : btn.textContent) : '';
        var TICK = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
        var fd = new FormData(form);
        var payload = { items: [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }] };
        var sp = fd.get('selling_plan');
        if (sp) payload.items[0].selling_plan = Number(sp);

        function restore() {
          if (!btn) return;
          if (isIcon) { btn.innerHTML = original; btn.classList.remove('is-added'); }
          else { btn.textContent = original; }
          btn.disabled = false;
        }

        if (btn) {
          btn.disabled = true;
          if (!isIcon) btn.textContent = 'Adding…';
        }
        Cart.add(payload)
          .then(function () {
            if (!btn) return;
            if (isIcon) { btn.innerHTML = TICK; btn.classList.add('is-added'); }
            else { btn.textContent = '✓ Added to Cart!'; }
            setTimeout(restore, 1800);
          })
          .catch(function (err) {
            restore();
            alert(err.message);
          });
      });
    });

    if (document.querySelector('[data-cart-drawer]')) Cart.fetch();
  }

  /* ---------- PDP: gallery + purchase options ----------------------------- */
  function initPdp() {
    var pdp = document.querySelector('[data-pdp]');
    if (!pdp) return;

    // Gallery thumbs
    var main = pdp.querySelector('[data-pdp-main]');
    pdp.querySelectorAll('[data-pdp-thumb]').forEach(function (t) {
      t.addEventListener('click', function () {
        var src = t.getAttribute('data-pdp-thumb');
        if (main) {
          main.src = src;
          main.style.animation = 'none';
          void main.offsetWidth; // restart the fade
          main.style.animation = 'fadeRise .4s var(--ease-out)';
        }
        pdp.querySelectorAll('[data-pdp-thumb]').forEach(function (o) { o.classList.remove('is-active'); });
        t.classList.add('is-active');
      });
    });

    // ---- Pack size + subscription plan ----------------------------------
    // Selling-plan prices are per-variant, so changing the pack size rebuilds
    // the purchase options from the JSON blob the section printed.
    var form = pdp.querySelector('[data-product-form]');
    if (!form) return;

    var dataEl = pdp.querySelector('[data-pdp-data]');
    var data = dataEl ? JSON.parse(dataEl.textContent) : { variants: [] };

    var variantInput = form.querySelector('[data-variant-id]');
    var planInput = form.querySelector('[data-selling-plan]');
    var planList = pdp.querySelector('[data-plan-list]');
    var priceEl = pdp.querySelector('[data-pdp-price]');
    var unitEl = pdp.querySelector('[data-pdp-unit]');
    var saveEl = pdp.querySelector('[data-pdp-save]');
    var addBtn = pdp.querySelector('[data-add-btn]');
    var accent = planList ? planList.getAttribute('data-accent') : 'var(--coral)';

    var current = data.variants[0];

    function paintPrice(unit, chargeNow, save, note) {
      if (priceEl) priceEl.textContent = Cart.formatMoney(unit);
      if (unitEl && current) unitEl.textContent = '/ ' + current.title.toLowerCase();
      if (addBtn) addBtn.textContent = current && !current.available
        ? 'Sold out'
        : 'Add to Cart — ' + Cart.formatMoney(chargeNow);
      if (!saveEl) return;
      if (save > 0) {
        saveEl.style.display = 'block';
        saveEl.textContent = 'You save ' + Cart.formatMoney(save) + ' per pack' + (note || '');
      } else {
        saveEl.style.display = 'none';
      }
    }

    function selectPlan(planId) {
      if (planInput) planInput.value = planId || '';
      var plan = null;
      for (var i = 0; i < current.plans.length; i++) {
        if (String(current.plans[i].id) === String(planId)) { plan = current.plans[i]; break; }
      }
      if (plan) {
        var note = plan.chargeNow !== plan.perDelivery
          ? ' · ' + Cart.formatMoney(plan.chargeNow) + ' charged upfront'
          : '';
        paintPrice(plan.perDelivery, plan.chargeNow, current.price - plan.perDelivery, note);
      } else {
        paintPrice(current.price, current.price, 0, '');
      }
      planList.querySelectorAll('[data-plan-option]').forEach(function (l) {
        l.classList.toggle('is-active', String(l.getAttribute('data-plan-id')) === String(planId || ''));
      });
    }

    function renderPlans() {
      if (!planList) return;
      var html = '' +
        '<label class="h8-plan-option is-active" data-plan-option data-plan-id="" style="--plan-accent:' + accent + '">' +
          '<input type="radio" name="purchase_option" value="" checked data-plan-radio>' +
          '<div>' +
            '<div class="h8-plan-label">One-time purchase</div>' +
            '<div class="h8-plan-note">' + Cart.formatMoney(current.price) + ' · No commitment</div>' +
          '</div>' +
        '</label>';

      // Cash on Delivery cannot bill a renewal, so a subscription in the cart leaves
      // Shopify with no payable method and checkout dies. Until a recurring-capable
      // gateway is live the plans stay visible but unselectable.
      var subsSoon = planList.getAttribute('data-subs-soon') === 'true';
      var soonLabel = planList.getAttribute('data-subs-soon-label') || 'Coming soon';

      current.plans.forEach(function (p, i) {
        var badge = subsSoon
          ? '<span class="h8-plan-best">' + soonLabel + '</span>'
          : ((i === current.plans.length - 1 && current.plans.length > 1)
              ? '<span class="h8-plan-best">Best value</span>' : '');
        html += '' +
          '<label class="h8-plan-option' + (subsSoon ? ' h8-format-soon' : '') + '" data-plan-option data-plan-id="' + p.id + '" style="--plan-accent:' + accent + '">' +
            '<input type="radio" name="purchase_option" value="' + p.id + '" data-plan-radio' + (subsSoon ? ' disabled' : '') + '>' +
            '<div>' +
              '<div class="h8-plan-label">' + p.name + '</div>' +
              '<div class="h8-plan-note">' + Cart.formatMoney(p.perDelivery) + ' · ' + p.desc + '</div>' +
            '</div>' + badge +
          '</label>';
      });

      planList.innerHTML = html;
      planList.querySelectorAll('[data-plan-radio]').forEach(function (r) {
        if (r.disabled) return;
        r.addEventListener('change', function () { selectPlan(r.value); });
      });
      selectPlan('');
    }

    function selectVariant(id) {
      for (var i = 0; i < data.variants.length; i++) {
        if (String(data.variants[i].id) === String(id)) { current = data.variants[i]; break; }
      }
      if (variantInput) variantInput.value = current.id;
      if (addBtn) addBtn.disabled = !current.available;
      pdp.querySelectorAll('[data-variant-option]').forEach(function (l) {
        var r = l.querySelector('[data-variant-radio]');
        l.classList.toggle('is-active', r && String(r.value) === String(current.id));
      });
      renderPlans();
    }

    pdp.querySelectorAll('[data-variant-radio]').forEach(function (r) {
      r.addEventListener('change', function () { selectVariant(r.value); });
    });

    var checkedVariant = pdp.querySelector('[data-variant-radio]:checked');
    selectVariant(checkedVariant ? checkedVariant.value : (current && current.id));
  }

  /* ---------- Newsletter: show inline success, keep Shopify's real submit -- */
  function initNewsletter() {
    document.querySelectorAll('[data-newsletter]').forEach(function (wrap) {
      // Shopify posts the form and returns with ?customer_posted=true
      if (window.location.search.indexOf('customer_posted=true') !== -1) {
        var form = wrap.querySelector('form');
        var done = wrap.querySelector('[data-newsletter-done]');
        if (form) form.style.display = 'none';
        if (done) done.style.display = 'inline-block';
      }
    });
  }

  /* ---------- boot --------------------------------------------------------- */
  function boot() {
    initReveal();
    initTwoTone();
    initHero();
    initFlavourRail();
    initInside();
    initAccordions();
    initNav();
    initCart();
    initPdp();
    initNewsletter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init inside the theme editor when a section is re-rendered.
  document.addEventListener('shopify:section:load', boot);

  window.hydr8Cart = Cart;
})();
